/* eslint-disable react-hooks/exhaustive-deps */
import React, { createContext, useState, useCallback, useMemo, useEffect, useContext } from "react";

export const DailyphasingContext = createContext();

export const DailyPhasingProvider = ({ children }) => {
    const [tableData, setTableData] = useState([]); // data for Table-1
    const [editableTableData, setEditableTableData] = useState([]); // data for Table-2
    const [editableTableFooter, SetEditableTableFooter] = useState([]);
    const [filteredTableData, setFilteredTableData] = useState([]); // filtered data for Table-1
    const [filteredEditableTableData, setFilteredEditableTableData] = useState([]); // filtered data for Table-2
    const [tableDataColumns, setTableDataColumns] = useState([]); // columns for Table-1
    const [editableTableDataColumns, setEditableTableDataColumns] = useState([]); // columns for Table-2
    const [globalFilters, setGlobalFilters] = useState([]); // global filters
    const [editableTableFilters, setEditableTableFilters] = useState([]); // editable table filters
    const [saveComments, setSaveComments] = useState([]);
    const [addedRows, setAddedRows] = useState([]); // for add new CBU
    const [thresoldValue, setThresoldValue] = useState([]); // for threshold
    const [thresholdBlocker, setThresholdBlocker] = useState(false); // for threshold check and if it is breached then save and submit btn disabled.
    const [btnDisable, setBtnDisable] = useState(false); // after changing line and cbu if it repeated then save submit will disable
    const [cascadeLineList, setCascadeLineList] = useState([]);
    const [duplicateRows, setDuplicateRows] = useState([]);
    const [idToken, setIdToken] = useState("");
    const [selectedWeek, setSelectedWeek] = useState(""); // Store selected week value

    const handleIdToken = useCallback((token) => {
        setIdToken(token);
    }, []);

    const weekChangeHandler = useCallback((week) => {
        setSelectedWeek(week);
    }, []);

    // Memoized filter application function
    const applyFilters = useCallback((data, filters) => {
        if (!Array.isArray(data) || data.length === 0) return data;
        return data.filter((item) => filters.every((filter) =>
            filter.selectedData.length ? filter.selectedData.includes(item[filter.label]) : true
        ));
    }, []);

    // Memoized function to update filter options
    const updateFilterOptions = useCallback((data, filters) => {
        if (!Array.isArray(data) || data.length === 0) return filters;
        return filters.map((filter) => {
            if (filter.selectedData.length) return filter;
            const updatedData = Array.from(new Set(data.map(item => item[filter.label]))).sort((a, b) => String(a).localeCompare(String(b)));
            return filter.data.length === updatedData.length && filter.data.every((val, i) => val === updatedData[i]) ? filter : { ...filter, data: updatedData };
        });
    }, []);

    // Global filters effect - applies filters to both tables
    useEffect(() => {
        if (!tableData.length || !editableTableData.length || !globalFilters.length) return;

        try {
            // Apply filters to both tables
            const tableFilteredData = applyFilters(tableData, globalFilters);

            // Update both filters based on the new filtered data (including addedRows and editableTableData)
            const allEditableData = [...addedRows, ...editableTableData];
            const updatedFilters = updateFilterOptions(allEditableData, globalFilters);
            localStorage.setItem("cbuLevelPlanFilters", JSON.stringify(updatedFilters));

            // Update filtered data
            setFilteredTableData(prev => JSON.stringify(prev) === JSON.stringify(tableFilteredData) ? prev : tableFilteredData);


            // Update global filters only if they have changed
            setGlobalFilters(prev => JSON.stringify(prev) === JSON.stringify(updatedFilters) ? prev : updatedFilters);
        } catch (err) {
            console.error("Global filter error:", err.message);
        }
    }, [globalFilters, tableData, editableTableData, addedRows, applyFilters, updateFilterOptions]);

    // Editable table filters effect - applies filters only to editable table
    useEffect(() => {
        if (!editableTableData.length || !editableTableFilters.length) return;

        try {
            const filteredData = applyFilters(applyFilters(editableTableData, editableTableFilters), globalFilters);
            const updatedFilters = updateFilterOptions(editableTableData, editableTableFilters);
            localStorage.setItem("lineCbuLevelPlanFilters", JSON.stringify(updatedFilters));

            setFilteredEditableTableData(prev => JSON.stringify(prev) === JSON.stringify(filteredData) ? prev : filteredData);
            setEditableTableFilters(prev => JSON.stringify(prev) === JSON.stringify(updatedFilters) ? prev : updatedFilters);

        } catch (err) {
            console.error("Editable filter error:", err.message);
        }
    }, [globalFilters, editableTableFilters, editableTableData, applyFilters, updateFilterOptions]);


    // Calculate footer data based on filtered editable table data
    useEffect(() => {
        if (!editableTableData.length) return;

        try {
            const filteredData = applyFilters(applyFilters(editableTableData, editableTableFilters), globalFilters);

            SetEditableTableFooter(prev => JSON.stringify(prev) === JSON.stringify(filteredData) ? prev : filteredData);

        } catch (err) {
            console.error("Editable filter error:", err.message);
        }
    }, [globalFilters, editableTableFilters, editableTableData, applyFilters]);



    // Memoized context value
    const contextValue = useMemo(() => ({
        tableData, setTableData,
        editableTableData, setEditableTableData,
        editableTableFooter,
        filteredTableData: filteredTableData,
        filteredEditableTableData: filteredEditableTableData,
        tableDataColumns, setTableDataColumns,
        editableTableDataColumns, setEditableTableDataColumns,
        thresoldValue, setThresoldValue,
        globalFilters,
        setGlobalFilters,
        editableTableFilters,
        setEditableTableFilters,
        addedRows, setAddedRows,
        saveComments, setSaveComments,
        btnDisable, setBtnDisable,
        thresholdBlocker, setThresholdBlocker,
        setFilteredTableData,setFilteredEditableTableData,
        cascadeLineList, setCascadeLineList,
        duplicateRows, setDuplicateRows,
        idToken, handleIdToken,
        selectedWeek, weekChangeHandler,
    }), [
        tableData,
        editableTableData,
        editableTableFooter,
        filteredTableData,
        filteredEditableTableData,
        tableDataColumns,
        editableTableDataColumns,
        globalFilters,
        editableTableFilters,
        thresoldValue,
        addedRows,
        saveComments,
        btnDisable,
        thresholdBlocker, duplicateRows,
        idToken, handleIdToken,
        selectedWeek, weekChangeHandler
    ]);

    return (
        <DailyphasingContext.Provider value={contextValue}>
            {children}
        </DailyphasingContext.Provider>
    );
}

export const useDailyPhasingContext = () => useContext(DailyphasingContext);